import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, 
  Package,
  ShoppingCart,
  Plane, 
  Truck, 
  Search,
  Eye,
  DollarSign,
  TrendingUp
} from 'lucide-react';
import { 
  EXPORT_WEEKLY_MAX_WEIGHT_KG, 
  EXPORT_WEEKLY_MAX_VOLUME_M3,
  getLimitColorStatus 
} from '../utils/orderLimits';

// Components
import ProductionPlanCard from '../components/Dashboard/ProductionPlanCard';
import PreparativosCard from '../components/Dashboard/PreparativosCard';

// Redux actions
import { fetchCustomers } from '../store/slices/customersSlice';
import { fetchProducts } from '../store/slices/productsSlice';
import { fetchOrders, fetchOrdersByWeek, setFilters } from '../store/slices/ordersSlice';
import { selectSeason, selectAvailableSeasons } from '../store/slices/seasonSlice';
import { 
  fetchPlanCosecha,
  selectPlanCosecha,
  selectPlanCosechaLoading
} from '../store/slices/planCosechaSlice';
import {
  fetchPreparativo,
  selectCurrentPreparativo,
  selectPreparativosLoading
} from '../store/slices/preparativosSlice';

// Selectors
import { 
  selectAllCustomers, 
  selectCustomersLoading, 
  selectCustomersError 
} from '../store/slices/customersSlice';
import { 
  selectAllProducts, 
  selectProductsLoading, 
  selectProductsError 
} from '../store/slices/productsSlice';
import { 
  selectAllOrders, 
  selectOrdersLoading, 
  selectOrdersError,
  selectWeekSummary 
} from '../store/slices/ordersSlice';

const SalesDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Estado local
  const [activeTab, setActiveTab] = useState('orders');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Selectores Redux
  const customers = useSelector(selectAllCustomers);
  const customersLoading = useSelector(selectCustomersLoading);
  const customersError = useSelector(selectCustomersError);
  
  const products = useSelector(selectAllProducts);
  const productsLoading = useSelector(selectProductsLoading);
  const productsError = useSelector(selectProductsError);
  
  const orders = useSelector(selectAllOrders);
  const ordersLoading = useSelector(selectOrdersLoading);
  const ordersError = useSelector(selectOrdersError);
  const weekSummary = useSelector(selectWeekSummary);
  const selectedSeason = useSelector(selectSeason);
  const seasons = useSelector(selectAvailableSeasons);
  
  // Production Plan selectors
  const plan = useSelector(selectPlanCosecha);
  const planLoading = useSelector(selectPlanCosechaLoading);
  
  // Preparativos selectors
  const preparativo = useSelector(selectCurrentPreparativo);
  const preparativosLoading = useSelector(selectPreparativosLoading);

  // Cargar datos al montar el componente
  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchProducts());
  }, [dispatch]);

  useEffect(() => {
    if (selectedSeason) {
      dispatch(
        fetchOrdersByWeek({
          year: selectedSeason.year,
          week: selectedSeason.week,
          filters: { search: '', status: 'all', type: 'all', priority: 'all', customerId: 'all' },
        })
      );
      
      // Fetch production plan for the selected week
      const temporada = seasons.find((item) => item.anio === selectedSeason.year);
      if (temporada?.id) {
        dispatch(fetchPlanCosecha({ temporadaId: temporada.id, week: selectedSeason.week }));
        // Fetch preparativos for the selected week
        dispatch(fetchPreparativo({ temporadaId: temporada.id, week: selectedSeason.week }));
      }
    } else {
      dispatch(fetchOrders({ search: '', status: 'all', type: 'all', priority: 'all', customerId: 'all' }));
    }
  }, [dispatch, selectedSeason, seasons]);

  // Filtrar datos según búsqueda
  const filteredCustomers = customers.filter(customer =>
    customer.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.codigo_cliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email_contacto?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProducts = products.filter(product =>
    product.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOrders = orders.filter(order =>
    order.clientes?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.productos?.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calcular estadísticas
  const exportOrders = weekSummary.exportOrders || { count: 0, totalBoxes: 0, totalWeight: 0, totalVolume: 0 };
  const nacionalOrders = weekSummary.nacionalOrders || { count: 0, boxesByType: {} };
  
  // Calculate total boxes and format breakdown for nacional orders
  const totalNacionalBoxes = Object.values(nacionalOrders.boxesByType).reduce((sum, count) => sum + count, 0);
  const boxTypesBreakdown = Object.keys(nacionalOrders.boxesByType).length > 0
    ? Object.entries(nacionalOrders.boxesByType)
        .map(([type, count]) => `${type}: ${count}`)
        .join(', ')
    : '';
  const boxTypesText = totalNacionalBoxes > 0 
    ? `${totalNacionalBoxes} cajas${boxTypesBreakdown ? ' (' + boxTypesBreakdown + ')' : ''}`
    : 'Sin cajas';

  // Format numbers in Chilean format
  const formatWeight = (kg) => {
    return Math.round(kg).toLocaleString('es-CL');
  };
  
  const formatVolume = (m3) => {
    return m3.toLocaleString('es-CL', { 
      minimumFractionDigits: 3, 
      maximumFractionDigits: 3 
    });
  };

  // Get limit status for export orders
  const weightStatus = getLimitColorStatus(exportOrders.totalWeight || 0, EXPORT_WEEKLY_MAX_WEIGHT_KG);
  const volumeStatus = getLimitColorStatus(exportOrders.totalVolume || 0, EXPORT_WEEKLY_MAX_VOLUME_M3);
  const weightPercentage = Math.round(((exportOrders.totalWeight || 0) / EXPORT_WEEKLY_MAX_WEIGHT_KG) * 100);
  const volumePercentage = Math.round(((exportOrders.totalVolume || 0) / EXPORT_WEEKLY_MAX_VOLUME_M3) * 100);

  const stats = [
    {
      name: 'Total Clientes',
      value: customers.length,
      icon: Users,
      change: '+12%',
      link: '/customers'
    },
    {
      name: 'Pedidos Exportación',
      value: exportOrders.count,
      subtitle: `${exportOrders.totalBoxes.toLocaleString('es-CL')} cajas`,
      subtitle2: `${formatWeight(exportOrders.totalWeight)} / ${EXPORT_WEEKLY_MAX_WEIGHT_KG.toLocaleString('es-CL')} kg (${weightPercentage}%)`,
      subtitle3: `${formatVolume(exportOrders.totalVolume)} / ${EXPORT_WEEKLY_MAX_VOLUME_M3.toLocaleString('es-CL')} m³ (${volumePercentage}%)`,
      icon: Plane,
      change: '+8%',
      link: '/orders',
      filterType: 'exportacion',
      weightStatus,
      volumeStatus
    },
    {
      name: 'Pedidos Nacional',
      value: nacionalOrders.count,
      subtitle: boxTypesText,
      icon: Truck,
      change: '+5%',
      link: '/orders',
      filterType: 'nacional'
    },
    {
      name: 'Ventas Semana',
      value: `$${weekSummary.totalAmountUSD?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`,
      subtitle: 'USD',
      subtitle2: `$${weekSummary.totalAmountCLP?.toLocaleString('es-CL') || '0'}`,
      subtitle3: 'CLP',
      icon: DollarSign,
      change: '+15%',
      link: '/orders'
    }
  ];

  const renderCustomersTable = () => (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>Código</th>
            <th>Nombre</th>
            <th>Contacto</th>
            <th>Email</th>
            <th>País</th>
            <th>Ciudad</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredCustomers.map((customer) => (
            <tr key={customer.id}>
              <td className="font-mono text-sm">{customer.codigo_cliente}</td>
              <td className="font-medium">{customer.nombre}</td>
              <td>{customer.nombre_contacto}</td>
              <td className="text-blue-600">{customer.email_contacto}</td>
              <td>{customer.pais}</td>
              <td>{customer.ciudad}</td>
              <td>
                <button className="btn btn-sm btn-outline">
                  <Eye size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderProductsTable = () => (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>Código</th>
            <th>Nombre</th>
            <th>Categoría</th>
            <th>Largo (cm)</th>
            <th>Peso Neto (kg)</th>
            <th>Precio FOB</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredProducts.map((product) => (
            <tr key={product.id}>
              <td className="font-mono text-sm">{product.code}</td>
              <td className="font-medium">{product.nombre}</td>
              <td>
                <span className="badge badge-secondary">{product.category}</span>
              </td>
              <td>{product.large_cm}</td>
              <td>{product.peso_neto_ruscus_kg}</td>
              <td className="font-medium text-green-600">
                ${product.precio_fob_mia?.toLocaleString('es-CL')}
              </td>
              <td>
                <button className="btn btn-sm btn-outline">
                  <Eye size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderOrdersTable = () => (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>Cliente</th>
            <th>Producto</th>
            <th>Cantidad</th>
            <th>Fecha Despacho</th>
            <th>Precio Total</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredOrders.map((order) => (
            <tr key={order.id}>
              <td>
                <div className="font-medium">{order.clientes?.nombre}</div>
                <div className="text-sm text-gray-500">{order.clientes?.codigo_cliente}</div>
              </td>
              <td>
                <div className="font-medium">{order.productos?.nombre}</div>
                <div className="text-sm text-gray-500">{order.productos?.code}</div>
              </td>
              <td>
                <span className="badge badge-info">{order.cantidad_cajas} cajas</span>
              </td>
              <td>
                {order.fecha_despacho ? 
                  new Date(order.fecha_despacho + 'T00:00:00').toLocaleDateString('es-CL')
                  : 'Sin fecha'
                }
              </td>
              <td className="font-medium text-green-600">
                ${order.precio_total?.toLocaleString('es-CL') || '0'}
              </td>
              <td>
                <button className="btn btn-sm btn-outline">
                  <Eye size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'customers':
        return customersLoading ? (
          <div className="loading-state">Cargando clientes...</div>
        ) : customersError ? (
          <div className="error-state">Error: {customersError}</div>
        ) : (
          renderCustomersTable()
        );
      case 'products':
        return productsLoading ? (
          <div className="loading-state">Cargando productos...</div>
        ) : productsError ? (
          <div className="error-state">Error: {productsError}</div>
        ) : (
          renderProductsTable()
        );
      case 'orders':
        return ordersLoading ? (
          <div className="loading-state">Cargando pedidos...</div>
        ) : ordersError ? (
          <div className="error-state">Error: {ordersError}</div>
        ) : (
          renderOrdersTable()
        );
      default:
        return null;
    }
  };

  return (
    <div className="sales-dashboard">
      {/* Header */}
      <div className="dashboard-header-card">
        <h1>Dashboard de Ventas y Operaciones</h1>
        <p>Gestión de clientes, pedidos, cosecha y exportación</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat) => {
          const Icon = stat.icon;
          
          const handleClick = (e) => {
            if (stat.filterType) {
              e.preventDefault();
              dispatch(setFilters({ clientType: stat.filterType }));
              navigate(stat.link);
            }
          };
          
          return (
            <Link 
              key={stat.name} 
              to={stat.link} 
              className="stat-card"
              onClick={handleClick}
            >
              <div className="stat-info">
                <p className="stat-name">{stat.name}</p>
                <p className="stat-value">{stat.value}</p>
                {stat.subtitle && (
                  <p className="stat-subtitle">{stat.subtitle}</p>
                )}
                {stat.subtitle2 && (
                  <p className={`stat-subtitle ${stat.weightStatus ? `limit-${stat.weightStatus}` : ''} ${stat.name === 'Ventas Semana' ? 'stat-value-secondary' : ''}`}>{stat.subtitle2}</p>
                )}
                {stat.subtitle3 && (
                  <p className={`stat-subtitle ${stat.volumeStatus ? `limit-${stat.volumeStatus}` : ''}`}>{stat.subtitle3}</p>
                )}
              </div>
              <div className="stat-icon">
                <Icon size={24} />
              </div>
              <div className="stat-footer">
                <TrendingUp size={16} />
                <span>{stat.change} vs semana anterior</span>
              </div>
            </Link>
          );
        })}
        
        {/* Production Plan Card */}
        <ProductionPlanCard 
          plan={plan}
          loading={planLoading}
          selectedSeason={selectedSeason}
        />
        
        {/* Preparativos Card */}
        <PreparativosCard
          preparativo={preparativo}
          loading={preparativosLoading}
          selectedSeason={selectedSeason}
        />
      </div>

      {/* Main Content */}
      <div className="card">
        {/* Tabs and Search */}
        <div className="content-header">
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              <ShoppingCart size={16} />
              Pedidos ({orders.length})
            </button>
            <button 
              className={`tab ${activeTab === 'customers' ? 'active' : ''}`}
              onClick={() => setActiveTab('customers')}
            >
              <Users size={16} />
              Clientes ({customers.length})
            </button>
            <button 
              className={`tab ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => setActiveTab('products')}
            >
              <Package size={16} />
              Productos ({products.length})
            </button>
          </div>
          
          <div className="search-bar">
            <Search size={16} />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>

      <style jsx>{`
        .sales-dashboard {
          max-width: 1400px;
          margin: 0 auto;
          padding: var(--spacing-6);
          display: flex;
          flex-direction: column;
          gap: var(--spacing-6);
        }

        @media (max-width: 768px) {
          .sales-dashboard {
            padding: var(--spacing-3);
            gap: var(--spacing-3);
          }
        }

        .dashboard-header-card {
          background: white;
          border-radius: var(--radius-lg);
          padding: var(--spacing-6);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--gray-200);
          margin-bottom: var(--spacing-6);
        }

        .dashboard-header-card h1 {
          font-size: 1.875rem;
          font-weight: 700;
          color: #2c5f2d;
          margin: 0 0 var(--spacing-2) 0;
        }

        .dashboard-header-card p {
          font-size: 1rem;
          color: #9ca3af;
          margin: 0;
        }

        @media (max-width: 768px) {
          .dashboard-header-card {
            padding: var(--spacing-4);
            margin-bottom: var(--spacing-3);
          }

          .dashboard-header-card h1 {
            font-size: 1.25rem;
          }

          .dashboard-header-card p {
            font-size: 0.875rem;
          }
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: var(--spacing-4);
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: var(--spacing-2);
          }
        }

        .stat-card {
          background: white;
          border-radius: var(--radius-lg);
          padding: var(--spacing-5);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--gray-200);
          text-decoration: none;
          color: inherit;
          transition: all 0.2s ease-in-out;
          display: flex;
          flex-direction: column;
          gap: var(--spacing-3);
          position: relative;
        }

        @media (max-width: 768px) {
          .stat-card {
            padding: var(--spacing-3);
            border-radius: var(--radius-md);
          }
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        .stat-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-2);
          align-items: flex-start;
          text-align: left;
          padding-right: 60px;
        }

        .stat-name {
          font-size: 0.875rem;
          color: #9ca3af;
          margin: 0;
          font-weight: 400;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
          line-height: 1;
        }

        .stat-subtitle {
          font-size: 0.75rem;
          color: #6b7280;
          margin: 0;
          font-weight: 400;
          line-height: 1.2;
        }

        .stat-subtitle.stat-value-secondary {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin-top: var(--spacing-2);
          line-height: 1;
        }

        .stat-subtitle.limit-success {
          color: #10b981;
          font-weight: 500;
        }

        .stat-subtitle.limit-warning {
          color: #f59e0b;
          font-weight: 600;
        }

        .stat-subtitle.limit-danger {
          color: #ef4444;
          font-weight: 700;
        }

        @media (max-width: 768px) {
          .stat-info {
            padding-right: 50px;
          }

          .stat-name {
            font-size: 0.75rem;
          }

          .stat-value {
            font-size: 1.25rem;
          }

          .stat-subtitle {
            font-size: 0.65rem;
          }

          .stat-subtitle.stat-value-secondary {
            font-size: 1.25rem;
          }
        }

        .stat-icon {
          position: absolute;
          top: var(--spacing-5);
          right: var(--spacing-5);
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #2c5f2d;
          color: white;
        }

        @media (max-width: 768px) {
          .stat-icon {
            width: 40px;
            height: 40px;
            top: var(--spacing-3);
            right: var(--spacing-3);
          }

          .stat-icon svg {
            width: 20px;
            height: 20px;
          }
        }

        .stat-footer {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: var(--spacing-1);
          color: #10b981;
          font-size: 0.875rem;
          font-weight: 400;
          margin-top: auto;
        }

        .stat-footer svg {
          width: 16px;
          height: 16px;
        }

        @media (max-width: 768px) {
          .stat-footer {
            font-size: 0.75rem;
          }

          .stat-footer svg {
            width: 14px;
            height: 14px;
          }
        }

        .content-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-6);
          gap: var(--spacing-4);
        }

        @media (max-width: 768px) {
          .content-header {
            margin-bottom: var(--spacing-3);
            gap: var(--spacing-2);
          }
        }

        .tabs {
          display: flex;
          gap: var(--spacing-1);
          background-color: var(--gray-100);
          padding: var(--spacing-1);
          border-radius: var(--radius-lg);
        }

        @media (max-width: 768px) {
          .tabs {
            width: 100%;
            padding: 2px;
            gap: 2px;
          }
        }

        .tab {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          padding: var(--spacing-3) var(--spacing-4);
          border: none;
          background: transparent;
          border-radius: var(--radius-md);
          font-weight: 500;
          color: var(--gray-600);
          cursor: pointer;
          transition: all 0.2s ease-in-out;
          white-space: nowrap;
        }

        @media (max-width: 768px) {
          .tab {
            flex: 1;
            padding: var(--spacing-2);
            gap: var(--spacing-1);
            font-size: 0.75rem;
            justify-content: center;
          }

          .tab svg {
            width: 14px;
            height: 14px;
          }
        }

        .tab:hover {
          color: var(--gray-900);
          background-color: var(--gray-200);
        }

        .tab.active {
          background-color: white;
          color: var(--primary-blue);
          box-shadow: var(--shadow-sm);
        }

        .search-bar {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          background-color: var(--gray-50);
          padding: var(--spacing-3);
          border-radius: var(--radius-lg);
          border: 1px solid var(--gray-200);
          min-width: 300px;
        }

        @media (max-width: 768px) {
          .search-bar {
            min-width: auto;
            padding: var(--spacing-2);
          }

          .search-bar svg {
            width: 14px;
            height: 14px;
          }
        }

        .search-input {
          border: none;
          background: transparent;
          outline: none;
          flex: 1;
          font-size: var(--font-size-sm);
        }

        .table-container {
          overflow-x: auto;
          border-radius: var(--radius-lg);
          border: 1px solid var(--gray-200);
        }

        .table {
          width: 100%;
          border-collapse: collapse;
        }

        .table th {
          background-color: var(--gray-50);
          padding: var(--spacing-4);
          text-align: left;
          font-weight: 600;
          color: var(--gray-700);
          border-bottom: 1px solid var(--gray-200);
        }

        .table td {
          padding: var(--spacing-4);
          border-bottom: 1px solid var(--gray-100);
        }

        .table tbody tr:hover {
          background-color: var(--gray-50);
        }

        .loading-state, .error-state {
          text-align: center;
          padding: var(--spacing-12);
          color: var(--gray-600);
        }

        .error-state {
          color: var(--red-600);
        }

        .card {
          background: white;
          border-radius: var(--radius-lg);
          padding: var(--spacing-6);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--gray-200);
        }

        @media (max-width: 768px) {
          .card {
            padding: var(--spacing-3);
            border-radius: var(--radius-md);
          }

          .content-header {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
};

export default SalesDashboard;
